var _excluded = ["cancelProps", "children", "classes", "closeFocusRef", "fullScreen", "fullScreenMobile", "messages", "onClose", "onRequestClose", "openFocusRef", "primaryContentSelector", "visible", "voiceOver"];

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import cs from 'classnames';
import PropTypes from 'prop-types';
import { closeIcon } from '@lululemon/ecom-pattern-library-icons';
import FocusTrap from '../../utils/focus-trap';
import { usePortal, useAriaHideSiblings, useHandleEscapeKey, usePreventBodyScroll } from '../../utils/hooks';
import Icon from '../icons';

require("./modal.css");

var styles = {
  "modalContainer": "modalContainer-1wlkX",
  "modal": "modal-2BrZH",
  "fullScreenContainer": "fullScreenContainer-351hz",
  "fullScreenModal": "fullScreenModal-2qtAz",
  "fullScreenMobileContainer": "fullScreenMobileContainer-35gj3",
  "fullScreenMobileModal": "fullScreenMobileModal-KVdQ4",
  "backdrop": "backdrop-3sgcn",
  "closeButton": "closeButton-1yRs1"
};
var canUseDOM = typeof document !== 'undefined';

var Modal = _ref => {
  var {
    cancelProps,
    children,
    classes,
    closeFocusRef,
    fullScreen,
    fullScreenMobile,
    messages,
    onClose,
    onRequestClose,
    openFocusRef,
    primaryContentSelector,
    visible,
    voiceOver
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var portalContainerRef = usePortal();
  useHandleEscapeKey(visible, onRequestClose);
  usePreventBodyScroll(visible);
  useAriaHideSiblings(visible, primaryContentSelector, portalContainerRef.current);
  var modalRef = useRef(); // onClose callback

  useEffect(() => {
    return () => {
      if (visible && onClose) {
        onClose();
      }
    };
  }, [visible, onClose]);

  if (!visible || !canUseDOM) {
    return null;
  }

  return (
    /*#__PURE__*/
    // Portal needs to be wrapped in a Fragment for
    // react docgen detection:
    // https://github.com/reactjs/react-docgen/issues/336
    React.createElement(React.Fragment, null, /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: cs(styles.backdrop, classes.backdrop)
    }), /*#__PURE__*/React.createElement("div", {
      "data-testid": "container",
      className: cs(styles.modalContainer, classes.container, {
        [styles.fullScreenContainer]: fullScreen,
        [styles.fullScreenMobileContainer]: fullScreenMobile
      }),
      onClick: ev => {
        // If click is directly on container, meaning it's
        // not on the modal or modal content, you've clicked
        // outside of the modal and can close it
        if (ev.target === ev.currentTarget) {
          onRequestClose(ev);
        }
      }
    }, /*#__PURE__*/React.createElement(FocusTrap, {
      openFocusRef: openFocusRef,
      closeFocusRef: closeFocusRef,
      childRef: modalRef
    }, /*#__PURE__*/React.createElement("div", _extends({
      "aria-label": voiceOver.title,
      "aria-modal": "true",
      role: "dialog",
      className: cs(styles.modal, classes.modal, {
        [styles.fullScreenModal]: fullScreen,
        [styles.fullScreenMobileModal]: fullScreenMobile
      }),
      ref: modalRef
    }, otherProps), /*#__PURE__*/React.createElement("button", _extends({
      type: "button",
      "aria-label": messages.close,
      className: styles.closeButton,
      onClick: onRequestClose
    }, cancelProps), /*#__PURE__*/React.createElement(Icon, {
      content: closeIcon
    })), /*#__PURE__*/React.createElement("div", {
      className: classes.modalContent
    }, children))))), portalContainerRef.current))
  );
};

Modal.propTypes = {
  /** Props set on the cancel button */
  // eslint-disable-next-line react/forbid-prop-types
  cancelProps: PropTypes.object,

  /** Passes modal content */
  children: PropTypes.node,

  /** CSS classNames applied to modal elements */
  classes: PropTypes.shape({
    modal: PropTypes.string,
    backdrop: PropTypes.string,
    container: PropTypes.string,
    modalContent: PropTypes.string
  }),

  /** Use with EXTREME caution.
   * Parameters around use are very narrow, if used incorrectly
   * this causes a11y errors.
   * Element ref for the element that should gain focus when
   * the modal closes. Defaults to the last focused element
   * (the button you clicked to open the modal, typically).
   */
  closeFocusRef: PropTypes.shape({
    current: PropTypes.object
  }),

  /** Flag to enable fullscreen styles on mobile and desktop browsers */
  fullScreen: PropTypes.bool,

  /**  Flag to enable fullscreen styles on mobile browsers */
  fullScreenMobile: PropTypes.bool,
  messages: PropTypes.shape({
    /** Label for the close button */
    close: PropTypes.string.isRequired
  }).isRequired,

  /** Callback fired when the modal closes */
  onClose: PropTypes.func,

  /** Callback fired when a user attempts to close the modal.
   * Use this to update the `visible` prop. */
  onRequestClose: PropTypes.func,

  /** Element ref for the element that should gain focus when
   * the modal opens. Defaults to the first tabbable element
   * in the modal (the close button, usually).
   */
  openFocusRef: PropTypes.shape({
    current: PropTypes.object
  }),

  /** A CSS selector to select your primary page content.
   * This content is hidden from screen readers while the
   * modal is open.
   */
  primaryContentSelector: PropTypes.string,

  /** Flag indicating whether the modal should display or not */
  visible: PropTypes.bool,
  voiceOver: PropTypes.shape({
    /** Label for the modal */
    title: PropTypes.string.isRequired
  })
};
Modal.defaultProps = {
  cancelProps: null,
  children: undefined,
  classes: {},
  closeFocusRef: undefined,
  fullScreenMobile: false,
  fullScreen: false,
  messages: {
    close: 'Close'
  },
  onClose: undefined,
  onRequestClose: undefined,
  openFocusRef: undefined,
  primaryContentSelector: 'body > *',
  visible: false,
  voiceOver: {
    title: 'Modal'
  }
};
export default Modal;